import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Tracks whether a scrollable element is currently at its top/bottom edge —
 * pair with `scroll-shadow.module.css`'s `.scrollShadowY` (via
 * `data-at-top`/`data-at-bottom` on the same element) to fade its edge
 * shadows in/out as an actual CSS `transition`, not just a scroll-linked
 * background trick. Re-measures on scroll AND on resize/content-length
 * change (a `ResizeObserver` on the element itself — catches e.g. a filter
 * shrinking the row count enough that the container stops overflowing).
 */
export function useScrollEdges<T extends HTMLElement>(scrollRef?: RefObject<T | null>) {
  const internalRef = useRef<T>(null);
  const ref = scrollRef ?? internalRef;
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setAtTop(scrollTop <= 0);
      setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, atTop, atBottom };
}
